import { Box, Card, CardContent, Grid, Skeleton, Tooltip, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EnqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { Controller, useForm } from "react-hook-form";
import DynamicSelect from "common/Select/Select";
import { useGetHealthKPIsService } from "subModule/src/services/analytics/useAnalytics";
import { GetHealthKPIsResponse } from "subModule/src/services/analytics/interface";
import { InfiniteData } from "@tanstack/react-query";
import InfoIcon from '@mui/icons-material/Info';
import {
  dashboardWidgetHeaderSx,
  dashboardWidgetFiltersSx,
  dashboardWidgetTitleRowSx,
} from "components/Analytics/dashboardWidgetLayout";
interface InventoryHealthKPIsProps {
  themes: any;
  stores: { value: number; label: string }[];
  enqueueSnackbar: EnqueueSnackbar;
  storesLoaded: boolean;
  handleDropdownActions: (typeName: string, action: "searchIt" | "loadNext", search?: string) => void;
  setDropdownPage: any;
  storesData: InfiniteData<any, unknown> | undefined;
  searchLoading: string | null;
  dropdownPage: number;
  dropdownSearchTerm: any;
  setDropdownSearchTerm: any;
  globalFilters: { storeId: any; allStore?: boolean; range: any };
  globalFilterApplied: boolean;
  setGlobalFilterApplied: (value: boolean) => void;
}
type HealthMetric = GetHealthKPIsResponse["data"]["metrics"][number];

const getMetricInfoParams = (metric: HealthMetric, t: (key: string) => string) => {
  const value = metric.value ?? 0;
  switch (metric.key) {
    case "availableStockValue":
    case "reservedStockValue":
      return { subtitle: metric.subtitle ?? "" };
    case "totalSkus":
      return { value: String(value) };
    case "lowStockItems":
      return { detail: value === 0 ? t("lowStockItemsInfoZero") : String(value) };
    case "outOfStock":
      return { detail: value === 0 ? t("outOfStockInfoZero") : String(value) };
    case "dailyConsumption":
      return { detail: value === 0 ? t("dailyConsumptionInfoZero") : String(value) };
    case "supplierLeadTime":
      return {
        value:
          metric.subtitle ??
          `${new Intl.NumberFormat("en", { maximumFractionDigits: 2 }).format(value)}${metric.badge?.text ? ` = ${metric.badge.text}` : ""}`,
      };
    default:
      return {};
  }
};

const InfoText = ({ metric }: { metric: HealthMetric }) => {
  const { t, i18n } = useTranslation();
  const infoParams = useMemo(() => getMetricInfoParams(metric, t), [metric, t, i18n.language]);
  return (
    <Typography className="font-Saira" sx={{ fontSize: '11px', color: "#111827", padding: "10px" }} dir={i18n.language === "ar" || i18n.language === "ur" ? "rtl" : "ltr"}>
      {t(`${metric.key}Info`, infoParams)}
    </Typography>
  );
};
const compactSelectSx = {
  "& .css-7896aj-control, & .css-1xiok1u-control": {
    minHeight: "auto",
    fontSize: '9px',
    height: "auto",
    overflow: "hidden",
    "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer": {
      background: "#fff",
      padding: 0,
      width: "15px",
      height: "15px",
    },
    "& .css-pogzpp-loadingIndicator, & .css-o9ehiq-loadingIndicator": { position: "absolute", right: 0 },
    border: 0,
  },
};

const getStatusStyles = (statusVariant: string) => {
  switch (String(statusVariant || "").toLowerCase()) {
    case "success":
      return { backgroundColor: "#D1E5DB", color: "#16A34A" };
    case "warning":
      return { backgroundColor: "#FEF3C7", color: "#D97706" };
    case "danger":
    case "error":
    case "critical":
      return { backgroundColor: "#FEE2E2", color: "#DC2626" };
    default:
      return { backgroundColor: "#F3F4F6", color: "#6B7280" };
  }
};
const formatMetricDisplay = (metric: any) => {
  const value = Math.abs(metric.value || 0);
  if (metric.unit === "percentage") {
    return `${new Intl.NumberFormat("en", { maximumFractionDigits: 2 }).format(value)}%`;
  }
  if (metric.unit === "currency") {
    return new Intl.NumberFormat("en", {
      notation: "compact",
      compactDisplay: "short",
      maximumFractionDigits: 2,
    }).format(value);
  }
  return new Intl.NumberFormat("en", {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 2,
  }).format(value);
};
const formatMetricTooltip = (metric: { value: number; unit: string }) => {
  const value = metric.value ?? 0;
  if (metric.unit === "percentage") return `${value.toLocaleString()}%`;
  return value.toLocaleString();
};

const InventoryHealthMetricCard = ({
  metric,
  label,
}: {
  metric: GetHealthKPIsResponse["data"]["metrics"][number];
  label: string;
}) => {
  const footer = metric.trend?.label
    ? { text: metric.trend.label, statusVariant: metric.trend.statusVariant }
    : metric.badge
      ? { text: metric.badge.text, statusVariant: metric.badge.statusVariant }
      : null;
  const footerStyles = footer ? getStatusStyles(footer.statusVariant) : null;
  const tooltipValue = formatMetricTooltip({ value: metric.value, unit: metric.unit });
  const displayValue = formatMetricDisplay(metric);

  return (
    <Box
      sx={{
        backgroundColor: "#FAF8F5",
        borderRadius: "8px",
        padding: "16px",
        height: "100%",
        minHeight: "96px",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        position: "relative",
      }}
    >
      <Tooltip title={<InfoText metric={metric} />}
        enterDelay={0} placement="top" arrow
        componentsProps={{ tooltip: { sx: { backgroundColor: '#fff', boxShadow: '0px 2px 8px rgba(0,0,0,0.12)' } } }}
        className="absolute top-1 right-1"
      >
        <InfoIcon sx={{ fontSize: 16, color: '#6b7280' }} />
      </Tooltip>
      <Tooltip followCursor title={label} enterDelay={0}>
        <Typography className="font-Saira truncate" sx={{ fontSize: '11px', color: "#6B7280", fontWeight: 500 }}>
          {label}
        </Typography>
      </Tooltip>
      <Tooltip followCursor title={tooltipValue} enterDelay={0}>
        <Typography className="font-Saira truncate" sx={{ fontSize: '19px', fontWeight: 700, color: "#111827", lineHeight: "24px" }}>
          {metric.unit === "currency" && metric.currency ? (
            <>
              {metric.currency}{" "}
              {(metric.value ?? 0) < 0 ? "-" : ""}
              {displayValue.replace(metric.currency, "").replace(/^-/, "")}
            </>
          ) : (
            displayValue
          )}
        </Typography>
      </Tooltip>
      {metric.subtitle && !footer && (
        <Typography className="font-Saira truncate" sx={{ fontSize: '10px', color: "#9CA3AF", marginTop: "4px" }}>
          {metric.subtitle}
        </Typography>
      )}
      {footer && footerStyles && (
        <Box
          component="span"
          sx={{
            display: "block",
            alignSelf: "flex-start",
            padding: "4px 6px 2px 6px",
            borderRadius: "4px",
            fontSize: '8px',
            fontWeight: 500,
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            margin: "4px auto 0 auto",
            ...footerStyles,
          }}
        >
          <Tooltip followCursor title={footer.text} enterDelay={0}>
            <span>{footer.text}</span>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default function InventoryHealthKPIs(props: InventoryHealthKPIsProps) {
  const { t, i18n } = useTranslation();
  const firstRender = useRef(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<GetHealthKPIsResponse["data"] | null>(null);
  const { control, formState: { errors }, setValue, watch } = useForm<{ [key: string]: any }>({
    defaultValues: {
      range: { value: 'monthly', label: t('mtd') },
    },
  });
  const filters = useMemo(() => [
    { value: 'daily', label: t('today') },
        { value: 'yesterday', label: t('yesterday') },
    { value: 'weekly', label: t('wtd') },
    { value: 'monthly', label: t('mtd') },
    { value: 'last_month', label: t('last_month') },
    { value: 'yearly', label: t('ytd') },
  ], [i18n.language]);
  const values = watch(["storeId", "range"]);
  const selectedStoreValue = Number(values[0]?.value ?? values[0]);
  const isAllStoreSelected = selectedStoreValue === 0 || props.globalFilters.allStore === true;
  const { refetch: apiRefetch } = useGetHealthKPIsService({
    storeId: isAllStoreSelected ? null : (values[0]?.value || null),
    allStore: isAllStoreSelected,
    range: values[1]?.value || null,
  });

  useEffect(() => {
    if (firstRender.current && props.storesLoaded && props.stores.length > 0) {
      firstRender.current = false;
      const defaultStore = (props.stores.length > 1 ? props.stores[1] : props.stores[0]) || null;
      setValue("storeId", defaultStore);
      setValue("range", props.globalFilters.range);
      setTimeout(() => fetchData(), 50);
    }
  }, [props.storesLoaded, props.stores]);
  useEffect(() => {
    const currentRange = watch("range");
    const updated = filters.find(f => f.value === currentRange?.value);
    if (updated) setValue("range", updated);
  }, [i18n.language, filters]);
  useEffect(() => {
    if (props.globalFilterApplied === true) {
      setValue("storeId", props.globalFilters.storeId);
      setValue("range", props.globalFilters.range);
      setTimeout(() => fetchData(), 100);
    }
  }, [props.globalFilterApplied]);

  const fetchData = async () => {
    setLoading(true);
    await apiRefetch()
      .then((res: any) => {
        if (res?.data?.success) {
          setData(res?.data?.data || null);
        } else {
          props.enqueueSnackbar(<AlertMessages text={res?.data?.message || "Something went wrong"} />, { variant: "error" });
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => {
        setLoading(false);
        if (props.globalFilterApplied === true) props.setGlobalFilterApplied(false);
      });
  };

  const metrics = data?.metrics || [];

  return (
    <Card sx={{ height: "310px", boxShadow: "none", borderRadius: "8px", border: "1px solid #E5E7EB", "& .MuiCardContent-root": { padding: "16px !important", height: "100%", boxSizing: "border-box" } }}>
      <CardContent sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={dashboardWidgetHeaderSx}>
          <Box sx={dashboardWidgetTitleRowSx}>
                    <Typography variant="h1" gutterBottom className="font-Saira text-start mb-0 mt-[5px]" sx={{ color: props.themes?.primary, fontSize: { xs: 12, sm: 14 }, fontWeight: { xs: 600, sm: 600 }, textAlign: "left" }}>
            {t("inventoryHealthKPIs")}
          </Typography>
                    </Box>
                    <Box sx={dashboardWidgetFiltersSx}>
            <Controller
              name="storeId"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box sx={compactSelectSx}>
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={props.stores}
                    isDisabled={loading}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.storeId?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    defaultPlaceholder={t("store")}
                    isLoading={props.searchLoading === "inventoryHealthKPIs"}
                    isSearchable
                    onInputChange={(search: string, { action }: { action: string }) => {
                      if (action === "input-change" || (action !== "set-value" && action !== "menu-close" && action !== "input-blur")) {
                        props.handleDropdownActions("inventoryHealthKPIs", "searchIt", search);
                      } else if (action === "input-blur" || action === "menu-close") {
                        if (props.dropdownSearchTerm || props.dropdownPage > 1) props.handleDropdownActions("inventoryHealthKPIs", "searchIt", "");
                        else props.setDropdownSearchTerm("");
                        props.setDropdownPage(1);
                      }
                    }}
                    onMenuScrollToBottom={() => {
                      if (!(props.storesData?.pages?.[0]?.data as any)?.last) props.handleDropdownActions("inventoryHealthKPIs", "loadNext");
                    }}
                    page={props.dropdownPage}
                  />
                </Box>
              )}
            />
            <Controller
              name="range"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box sx={compactSelectSx}>
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={filters}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.range?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    isDisabled={loading}
                  />
                </Box>
              )}
            />
          </Box>
        </Box>
        {loading ? (
          <Box sx={{ flex: 1, overflow: "hidden", marginTop: "5px" }}>
            <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
            <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
            <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
            <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
            <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
            <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
            <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
          </Box>
        ) : metrics.length === 0 ? (
          <Box className="flex justify-center flex-col flex-1">
            <Typography className="font-Saira text-center italic font-semibold" sx={{ color: props.themes.primary }}>
              {t("noRecordFound")}
            </Typography>
          </Box>
        ) : (
          <Box className="max-h-[230px] overflow-y-auto mt-[8px] pr-[6px]">
            <Grid container spacing={2}>
              {metrics.map((metric) => (
                <Grid item xs={12} sm={6} md={3} key={metric.key}>
                  <InventoryHealthMetricCard metric={metric} label={t(metric.key) || metric.label} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
